import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ApiError, apiGet } from "../lib/api";
import { cn } from "../lib/utils";
import {
  type CatalogueBambu,
  catalogueBambuSchema,
  type Reglages,
  type RenduReponse,
  type SerreLivresRequete,
  type SilhouettesInfo,
  type TeinteBambu,
} from "./contract";
import {
  ALERTE,
  ApercuRendu,
  BOUTON_SECONDAIRE,
  CARTE,
  CarteImage,
  ErreurDuRendu,
  type ImageChoisie,
  TitreCarte,
  useUrlLocale,
} from "./silhouettes";
import { Etagere } from "./silhouettes-etagere";
import { useLiensDeFichiers } from "./silhouettes-fichiers";
import {
  cleDeChoix,
  figuresAuDelaDeLAms,
  filamentsDeLaFigure,
  filamentsDuJeu,
  type LigneFilament,
  lignes,
  lireMemoire,
  retenir,
  teinteBaseParDefaut,
  teintesDeLaRequete,
} from "./silhouettes-filaments";
import { ErreurImage, reduireImage } from "./silhouettes-image";
import { lireNom, type NomLu, ROUTE_BAMBU } from "./silhouettes-jeux";
import {
  appliquer,
  CarteReglages,
  CHAMP,
  type Lecture,
  Ligne,
  lireSaisie,
  type Saisie,
  saisieDepuis,
} from "./silhouettes-reglages";
import { type ImageEnvoi, type Rendu, useRendu } from "./silhouettes-rendu";
import { CarteFichiers, Roue } from "./silhouettes-serre-livres-assemblage";
import { CarteFilaments, type CatalogueVu } from "./silhouettes-serre-livres-filaments";
import {
  aideDuNom,
  erreurDuNom,
  exempleDeNom,
  figurineDe,
  LIBELLE_SENS_EN_CALCUL,
  legendeDeLaColonne,
  legendeDuMiroir,
  legendeHorsTout,
  libelleGarderSonSens,
  libelleRetourner,
  libelleRetournerA,
  MENTION_MIROIR,
  nomOuColonne,
  pastilleDuVerdict,
  raisonDesFichiersEteints,
  resumeDeLaColonneRepliee,
  resumeDesReglages,
  texteDuVerdict,
  titreDeLaColonne,
} from "./silhouettes-serre-livres-textes";
import { nettoyerNomFichier } from "./silhouettes-textes";
import { type Colonne, coteMur, type Verdict, verdict } from "./silhouettes-verdict";

/**
 * Le mode serre-livres de la page Silhouettes (#337, spec du 4 octobre 2026 `docs/specs/
 * 2026-10-04-silhouettes-serre-livres-design.md`, maquettes `docs/maquettes/console-lab/
 * SilhouettesSerreLivres*.dc.html`) : deux figurines, celle de gauche et celle de droite, chacune
 * rendue comme une silhouette, jugee dans son Bookend, posee sur une etagere, puis deux jeux
 * complets (P1S et A1 mini) assembles par `products`.
 *
 * **Une figurine est un emplacement, pas une colonne.** Chacune a son image, ses reglages, son nom,
 * son miroir et son rendu (un `useRendu` par emplacement) ; la colonne n'est que l'endroit ou elle
 * est posee, et ⇄ echange les deux. Le rendu d'une figurine ne depend de la colonne que par
 * `cote_mur`, le cote de sa vue de face tourne vers le mur : un echange le change et relance le
 * calcul de la hauteur qui garde le sens, mais le verdict et l'etagere relisent tout de suite les
 * deux debords deja mesures (`silhouettes-verdict`). Les colonnes sont rendues avec la cle de leur
 * figurine : en changeant de colonne, une figurine garde son rendu, ses reglages et son etat replie.
 *
 * Deux modules purs servent la page : `silhouettes-jeux` (nom, plateaux, requete, echec) et
 * `silhouettes-serre-livres-textes` (ce que la page dit). Le DOM suit l'ordre de la maquette :
 * colonnes, etagere, filaments, fichiers.
 *
 * Ce fichier reprend de `silhouettes.tsx` (la page d'une silhouette) sa zone de depot, son apercu
 * et ses gabarits de cartes ; `silhouettes.tsx` ne l'importe pas : c'est `silhouettes-page.tsx`,
 * la page, qui choisit entre les deux.
 */

/**
 * Le nom que chaque figurine donne a l'outil pour son rendu : un nom de fichier valide pour lui
 * (`reglagesSchema`), jamais le nom qu'on affiche, qui n'est choisi qu'apres coup et n'a rien a
 * faire dans le calcul.
 */
const NOM_DU_RENDU = ["figurine-1", "figurine-2"] as const;

/**
 * La hauteur de depart de chaque figurine : 93 mm, soit 97 hors tout avec la barre de 4 mm, celle
 * des planches (decision de Nicolas, le 5 octobre 2026). La page d'une silhouette garde les 121 mm
 * des reglages par defaut du module : une figurine de serre-livres, a cette hauteur, depasserait
 * presque toujours de son Bookend.
 */
const HAUTEUR_DE_DEPART_MM = 93;

type Emplacement = 0 | 1;

type Figure = {
  index: Emplacement;
  image: ImageChoisie | null;
  erreurImage: string | null;
  preparation: boolean;
  choisir: (fichier: File) => void;
  boutonChanger: RefObject<HTMLButtonElement | null>;
  saisie: Saisie;
  setSaisie: Dispatch<SetStateAction<Saisie>>;
  /** Ce que la saisie donne, hors le nom : celui qu'on affiche n'est pas celui du rendu. */
  lecture: Lecture;
  miroir: boolean;
  setMiroir: (miroir: boolean) => void;
  rendu: Rendu;
  /** Les corrections de teinte de cette figurine : nom de la couleur trouvee vers cle du catalogue. */
  choix: Record<string, string>;
  corriger: (couleur: string, cle: string) => void;
  /** La memoire du navigateur telle qu'elle etait quand l'image de la figurine est arrivee. */
  memoire: Record<string, string>;
};

/**
 * Ce que le navigateur a retenu des teintes, tel qu'il l'etait **quand l'image de la figurine est
 * arrivee** (choisie, ou changee), et non a chaque rendu de la meme image : un miroir, une hauteur
 * ou « Garder son sens » sur une image deja a l'ecran ne la recolorent jamais d'apres ce que Nicolas
 * a corrige dans l'autre colonne. La memoire est par nom de couleur, les corrections de la page par
 * colonne : en la relisant a chaque `retenir`, ou a chaque rendu, corriger le « blanc » de gauche
 * changerait aussi celui de droite sous la main de Nicolas. Elle est proposee d'abord a la prochaine
 * image, comme le dit la spec. Un instantane par figurine.
 *
 * Relue pendant le rendu, quand l'image change (l'etat se corrige de lui-meme, sans un rendu de
 * trop avec l'ancienne memoire) plutot que dans un effet.
 */
function useMemoireDesTeintes(image: ImageChoisie | null): Record<string, string> {
  const [vue, setVue] = useState(() => ({ image, memoire: lireMemoire() }));
  if (vue.image === image) return vue.memoire;
  const suivante = { image, memoire: lireMemoire() };
  setVue(suivante);
  return suivante.memoire;
}

/**
 * Une figurine : son image (choisie, reduite, avec la course entre deux choix qu'a la page d'une
 * silhouette), sa saisie, son miroir, son rendu pour la colonne ou elle est posee, ses corrections
 * de teinte.
 */
function useFigure(
  infos: SilhouettesInfo,
  index: Emplacement,
  colonne: Colonne,
  surRefus: (erreur: ApiError) => void,
): Figure {
  const [image, setImage] = useState<ImageChoisie | null>(null);
  const [erreurImage, setErreurImage] = useState<string | null>(null);
  const [preparation, setPreparation] = useState(false);
  // Le nom commence vide : il nomme la piece et le plateau, il ne vient jamais du nom du fichier.
  const [saisie, setSaisie] = useState<Saisie>(() =>
    saisieDepuis({ ...infos.reglages, hauteur_mm: HAUTEUR_DE_DEPART_MM }),
  );
  const [miroir, setMiroir] = useState(false);
  const [choix, setChoix] = useState<Record<string, string>>({});
  const boutonChanger = useRef<HTMLButtonElement>(null);
  // Le numero du dernier fichier choisi : une reduction plus lente qu'une autre, choisie apres, ne
  // doit pas la remplacer en finissant.
  const dernierChoix = useRef(0);

  const envoi = useMemo<ImageEnvoi | null>(
    () =>
      image ? { base64: image.base64, nomFichier: nettoyerNomFichier(image.nomFichier) } : null,
    [image],
  );
  // L'outil juge un nom de fichier, pas le nom affiche : on lui en donne un qui lui convient, et
  // l'attache est toujours la base modulaire, celle du Bookend.
  const lecture = useMemo(
    () => lireSaisie({ ...saisie, nom: NOM_DU_RENDU[index], attache: true }),
    [saisie, index],
  );
  const cote = coteMur(colonne, miroir);
  const reglages = useMemo<Reglages>(
    () => ({ ...lecture.reglages, cote_mur: cote }),
    [lecture, cote],
  );
  const rendu = useRendu(envoi, reglages, lecture.valides);
  const memoire = useMemoireDesTeintes(image);

  // Le bouton qui ouvrait le choix du fichier disparait avec la zone de depot : le focus passe a
  // « Changer d'image », dans la meme zone, plutot que de retomber sur la page.
  useEffect(() => {
    if (image) boutonChanger.current?.focus();
  }, [image]);

  useEffect(() => {
    const statut = rendu.erreur?.status;
    if (rendu.erreur && (statut === 401 || statut === 403)) surRefus(rendu.erreur);
  }, [rendu.erreur, surRefus]);

  const choisir = async (fichier: File): Promise<void> => {
    dernierChoix.current += 1;
    const choisi = dernierChoix.current;
    setPreparation(true);
    try {
      const reduite = await reduireImage(fichier);
      if (choisi !== dernierChoix.current) return;
      setErreurImage(null);
      setImage({ ...reduite, nomFichier: fichier.name, fichier });
      // Une autre image a d'autres couleurs : les corrections de la precedente ne la concernent pas.
      setChoix({});
      // Ni les couleurs dont elle epaississait les traits (#397) : leurs noms ne valent que pour elle.
      setSaisie((actuelle) => ({ ...actuelle, couleursEpaissies: [] }));
    } catch (erreur) {
      if (choisi !== dernierChoix.current) return;
      // L'image precedente reste, s'il y en avait une : rien n'est perdu.
      setErreurImage(
        erreur instanceof ErreurImage ? erreur.message : `${fichier.name} n'a pas pu être lue.`,
      );
    } finally {
      if (choisi === dernierChoix.current) setPreparation(false);
    }
  };

  return {
    index,
    image,
    erreurImage,
    preparation,
    choisir: (fichier) => void choisir(fichier),
    boutonChanger,
    saisie,
    setSaisie,
    lecture,
    miroir,
    setMiroir,
    rendu,
    choix,
    // Chaque correction est retenue pour les rendus suivants, et entre dans les choix de la page.
    corriger: (couleur, cle) => {
      setChoix((actuels) => ({ ...actuels, [couleur]: cle }));
      retenir(couleur, cle);
    },
    memoire,
  };
}

/**
 * `GET /api/silhouettes/bambu`, a l'entree dans le mode : le catalogue ne change pas pendant la
 * session, une lecture qui a reussi suffit. La page d'une silhouette n'en depend pas.
 */
function useCatalogueBambu(): UseQueryResult<CatalogueBambu, Error> {
  return useQuery({
    queryKey: ["silhouettes", "bambu"],
    queryFn: () => apiGet(ROUTE_BAMBU, catalogueBambuSchema),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** Les corrections d'une figurine, dans la forme que `lignes` lit : la colonne devant le nom de la couleur. */
function choixParColonne(colonne: Colonne, choix: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(choix).map(([couleur, cle]) => [cleDeChoix(colonne, couleur), cle]),
  );
}

/** Le 3MF d'un rendu : celui que l'assemblage relit, tel quel, en base64. */
function troisMf(reponse: RenduReponse | null): string | null {
  return reponse?.fichiers.find((fichier) => fichier.nom.endsWith(".3mf"))?.contenu ?? null;
}

export function SerreLivres({
  infos,
  surRefus,
}: {
  infos: SilhouettesInfo;
  surRefus: (erreur: ApiError) => void;
}) {
  const [echange, setEchange] = useState(false);
  const [baseChoisie, setBaseChoisie] = useState<string | null>(null);
  const catalogue = useCatalogueBambu();
  const premiere = useFigure(infos, 0, echange ? "droite" : "gauche", surRefus);
  const seconde = useFigure(infos, 1, echange ? "gauche" : "droite", surRefus);
  const gauche = echange ? seconde : premiere;
  const droite = echange ? premiere : seconde;
  const figures: Record<Colonne, Figure> = { gauche, droite };

  // Une session perdue en lisant le catalogue remplace la page, comme a l'ouverture.
  useEffect(() => {
    const erreur = catalogue.error;
    if (erreur instanceof ApiError && (erreur.status === 401 || erreur.status === 403)) {
      surRefus(erreur);
    }
  }, [catalogue.error, surRefus]);

  const donneesDuCatalogue = catalogue.data;
  // Une relecture de ce qui n'a jamais reussi repasse la requete en attente (`error` videe) : la
  // carte dit alors que les teintes se chargent, et plus qu'elles ne repondent pas.
  const catalogueVu: CatalogueVu =
    donneesDuCatalogue !== undefined
      ? { etat: "pret", catalogue: donneesDuCatalogue }
      : catalogue.isError
        ? { etat: "erreur", reessayer: () => void catalogue.refetch() }
        : { etat: "attente" };

  // --- Ce que la page sait des deux figurines ---------------------------------------------------
  const reponseGauche = gauche.rendu.reponse;
  const reponseDroite = droite.rendu.reponse;
  const rapportGauche = reponseGauche?.rapport ?? null;
  const rapportDroite = reponseDroite?.rapport ?? null;
  const { memoire: memoireGauche, choix: choixGauche } = gauche;
  const { memoire: memoireDroite, choix: choixDroite } = droite;
  // `lignes` ne lit qu'une memoire : celle de chaque figurine, une figurine apres l'autre.
  const toutes = useMemo<LigneFilament[]>(
    () =>
      donneesDuCatalogue === undefined
        ? []
        : [
            ...lignes(
              { gauche: rapportGauche, droite: null },
              donneesDuCatalogue,
              memoireGauche,
              choixParColonne("gauche", choixGauche),
            ),
            ...lignes(
              { gauche: null, droite: rapportDroite },
              donneesDuCatalogue,
              memoireDroite,
              choixParColonne("droite", choixDroite),
            ),
          ],
    [
      donneesDuCatalogue,
      rapportGauche,
      rapportDroite,
      memoireGauche,
      memoireDroite,
      choixGauche,
      choixDroite,
    ],
  );
  const teinteBase: TeinteBambu | null =
    donneesDuCatalogue === undefined
      ? null
      : (donneesDuCatalogue.teintes.find((teinte) => teinte.cle === baseChoisie) ??
        teinteBaseParDefaut(toutes, donneesDuCatalogue));
  const noms: Record<Colonne, NomLu> = {
    gauche: lireNom(gauche.saisie.nom),
    droite: lireNom(droite.saisie.nom),
  };
  const nomsAffiches: Record<Colonne, string> = {
    gauche: nomOuColonne(noms.gauche, "gauche"),
    droite: nomOuColonne(noms.droite, "droite"),
  };
  const nombres: Record<Colonne, number> = {
    gauche: filamentsDeLaFigure(toutes, "gauche"),
    droite: filamentsDeLaFigure(toutes, "droite"),
  };
  const verdicts: Record<Colonne, Verdict | null> = {
    gauche: rapportGauche ? verdict(rapportGauche, "gauche", gauche.miroir) : null,
    droite: rapportDroite ? verdict(rapportDroite, "droite", droite.miroir) : null,
  };

  // --- Les fichiers : une requete, ou la raison de s'en passer ----------------------------------
  // Les fichiers ne s'offrent que s'ils repondent aux reglages affiches, comme sur la page d'une
  // silhouette : un rendu a jour, et des reglages valides.
  const rendusPrets = [gauche, droite].every(
    (figure) =>
      figure.rendu.etat === "pret" && figure.rendu.reponse !== null && figure.lecture.valides,
  );
  const raison = raisonDesFichiersEteints({
    images: { gauche: gauche.image !== null, droite: droite.image !== null },
    noms,
    catalogue: catalogueVu.etat,
    rendusPrets,
  });
  const nomGauche = noms.gauche.etat === "valide" ? noms.gauche.nom : null;
  const nomDroite = noms.droite.etat === "valide" ? noms.droite.nom : null;
  const { miroir: miroirGauche } = gauche;
  const { miroir: miroirDroite } = droite;
  const requete = useMemo<SerreLivresRequete | null>(() => {
    const fichierGauche = troisMf(reponseGauche);
    const fichierDroite = troisMf(reponseDroite);
    if (raison !== null || !teinteBase || !fichierGauche || !fichierDroite) return null;
    if (!nomGauche || !nomDroite) return null;
    return {
      gauche: {
        fichier_3mf: fichierGauche,
        nom: nomGauche,
        miroir: miroirGauche,
        teintes: teintesDeLaRequete(toutes, "gauche"),
      },
      droite: {
        fichier_3mf: fichierDroite,
        nom: nomDroite,
        miroir: miroirDroite,
        teintes: teintesDeLaRequete(toutes, "droite"),
      },
      teinte_base: teinteBase.cle,
    };
  }, [
    raison,
    teinteBase,
    reponseGauche,
    reponseDroite,
    nomGauche,
    nomDroite,
    miroirGauche,
    miroirDroite,
    toutes,
  ]);

  // Un rendu tourne : un 3MF illisible, dont la carte dit « relance… », a encore quelque chose a attendre.
  const rendusEnCours = gauche.rendu.etat === "calcul" || droite.rendu.etat === "calcul";

  const relancerLesRendus = (): void => {
    gauche.rendu.relancer();
    droite.rendu.relancer();
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_44px_minmax(0,1fr)] lg:items-start">
        <ColonneDeFigurine
          colonne="gauche"
          figure={gauche}
          filaments={nombres.gauche}
          key={gauche.index}
          nom={noms.gauche}
          verdict={verdicts.gauche}
        />
        <BoutonEchanger echanger={() => setEchange((actuel) => !actuel)} key="echanger" />
        <ColonneDeFigurine
          colonne="droite"
          figure={droite}
          filaments={nombres.droite}
          key={droite.index}
          nom={noms.droite}
          verdict={verdicts.droite}
        />
      </div>

      <CarteEtagere
        droite={reponseDroite}
        gauche={reponseGauche}
        miroirDroite={miroirDroite}
        miroirGauche={miroirGauche}
        teinteBase={teinteBase}
        toutes={toutes}
      />

      <CarteFilaments
        ams={{ colonnes: figuresAuDelaDeLAms(toutes), nombres }}
        catalogue={catalogueVu}
        choisirLaBase={setBaseChoisie}
        corriger={(colonne, couleur, cle) => figures[colonne].corriger(couleur, cle)}
        filamentsDuJeu={teinteBase ? filamentsDuJeu(toutes, teinteBase) : []}
        lignes={toutes}
        noms={nomsAffiches}
        teinteBase={teinteBase}
      />

      <CarteFichiers
        filaments={nombres}
        noms={nomsAffiches}
        raison={raison}
        relancerLesRendus={relancerLesRendus}
        rendusEnCours={rendusEnCours}
        requete={requete}
        surRefus={surRefus}
      />
    </>
  );
}

/** Le bouton ⇄ : entre les deux colonnes sur grand ecran, pleine largeur entre les deux cartes au telephone. */
function BoutonEchanger({ echanger }: { echanger: () => void }) {
  return (
    <div className="flex justify-center lg:pt-32">
      <button
        aria-label="Échanger gauche et droite"
        className={cn(BOUTON_SECONDAIRE, "min-h-11 w-full gap-2 lg:size-10 lg:min-h-0 lg:px-0")}
        onClick={echanger}
        title="Échanger gauche et droite"
        type="button"
      >
        <span aria-hidden="true">⇄</span>
        <span className="lg:hidden">Échanger gauche et droite</span>
      </button>
    </div>
  );
}

/** Le chevron du repli : vers le haut quand la colonne est depliee, vers le bas quand elle est repliee. */
function Chevron({ haut }: { haut: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={cn("size-4 shrink-0", haut && "rotate-180")}
      fill="none"
      viewBox="0 0 16 16"
    >
      <path
        d="M3.5 6l4.5 4.5L12.5 6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

/** L'encadre vert du verdict qui entre : la teinte « bonne » du coloriage, comme `ALERTE.rouge` est le refus. */
const ALERTE_VERTE =
  "rounded-md border border-jugement-bonne/40 bg-jugement-bonne/10 px-3.5 py-2.5 text-sm text-jugement-bonne";

/** Un remede : un bouton de contour dans l'encadre du verdict, de 44 px de haut au telephone. */
const BOUTON_REMEDE = "min-h-11 px-3 text-[13px] md:min-h-8 md:px-2.5";

/**
 * Le verdict d'une figurine dans son Bookend, et ses remedes (spec, section 2). Chaque bouton
 * applique ce qu'il dit : « Retourner » bascule le miroir (il arrive allume : le remede est alors de
 * l'eteindre), « Garder son sens » regle la hauteur de silhouette telle quelle, « Retourner a M mm »
 * fait les deux. La hauteur qui garde le sens attend le rendu qui la cherche pour le nouveau cote
 * quand le miroir ou l'echange vient de le changer.
 */
function BlocDuVerdict({
  verdict: v,
  enCalcul,
  retourner,
  retournerA,
  garder,
}: {
  verdict: Verdict;
  enCalcul: boolean;
  retourner: () => void;
  retournerA: (hauteur: number) => void;
  garder: (hauteur: number) => void;
}) {
  const texte = texteDuVerdict(v);
  const classe =
    texte.ton === "bon" ? ALERTE_VERTE : texte.ton === "mauvais" ? ALERTE.rouge : ALERTE.grise;

  // Les deux verdicts qui n'entrent pas ont un remede en commun : garder le sens.
  const nEntrePas = v.etat === "retourner" || v.etat === "aucun-sens" ? v : null;
  const hauteurSens = nEntrePas?.hauteurSens ?? null;
  // La hauteur du cote n'est celle du cote tourne vers le mur que si le rendu l'a cherchee pour
  // lui : apres un miroir, elle arrive avec le rendu qui part.
  const attendLeCote =
    nEntrePas !== null && hauteurSens === null && !nEntrePas.sensAJour && enCalcul;
  const hauteurRetourne = v.etat === "aucun-sens" ? v.hauteurRetourne : null;

  const boutonGarder =
    hauteurSens !== null ? (
      <button
        className={cn(BOUTON_SECONDAIRE, BOUTON_REMEDE)}
        key="garder"
        onClick={() => garder(hauteurSens)}
        type="button"
      >
        {libelleGarderSonSens(hauteurSens)}
      </button>
    ) : attendLeCote ? (
      <button
        className={cn(BOUTON_SECONDAIRE, BOUTON_REMEDE, "disabled:opacity-60")}
        disabled
        key="garder"
        type="button"
      >
        <Roue />
        {LIBELLE_SENS_EN_CALCUL}
      </button>
    ) : null;
  const boutonRetourner =
    v.etat === "retourner" ? (
      <button
        className={cn(BOUTON_SECONDAIRE, BOUTON_REMEDE)}
        key="retourner"
        onClick={retourner}
        type="button"
      >
        {libelleRetourner(v.debordRetourne)}
      </button>
    ) : hauteurRetourne !== null ? (
      <button
        className={cn(BOUTON_SECONDAIRE, BOUTON_REMEDE)}
        key="retourner"
        onClick={() => retournerA(hauteurRetourne)}
        type="button"
      >
        {libelleRetournerA(hauteurRetourne)}
      </button>
    ) : null;
  // « Retourner » d'abord quand c'est le remede evident, « Garder son sens » d'abord quand rien
  // n'entre : l'ordre des planches.
  const remedes =
    v.etat === "retourner" ? [boutonRetourner, boutonGarder] : [boutonGarder, boutonRetourner];

  return (
    <div className={classe} data-verdict={v.etat}>
      <p role="status">
        <strong className="font-semibold">{texte.titre}</strong> {texte.detail}
      </p>
      {boutonGarder || boutonRetourner ? (
        <div className="mt-2 flex flex-col gap-2 md:flex-row md:flex-wrap">{remedes}</div>
      ) : null}
    </div>
  );
}

/**
 * L'interrupteur de la maquette : celui de « garder » dans les couleurs, pour le miroir. Son pseudo-
 * element `after` agrandit la zone qui repond au doigt de 14 px tout autour : 48 px de haut, au lieu
 * des 20 de l'interrupteur dessine.
 */
const INTERRUPTEUR = cn(
  "relative h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-muted-foreground transition-colors checked:bg-primary after:absolute after:-inset-3.5 after:content-['']",
  "before:absolute before:top-[3px] before:left-[3px] before:h-3.5 before:w-3.5 before:rounded-full before:bg-card before:transition-transform checked:before:translate-x-4",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
);

/**
 * La colonne d'une figurine : son image, ses reglages (dont le nom et le miroir), l'apercu de
 * l'outil et le verdict. Au telephone, un chevron la replie en une ligne (le nom, la hauteur et le
 * verdict) ; a partir de `lg`, elle est toujours depliee.
 */
function ColonneDeFigurine({
  colonne,
  figure,
  nom,
  verdict: verdictDeLaColonne,
  filaments,
}: {
  colonne: Colonne;
  figure: Figure;
  nom: NomLu;
  verdict: Verdict | null;
  /** Les filaments de la figurine apres les teintes choisies, pour le nom de son plateau ; `0` avant son rendu. */
  filaments: number;
}) {
  const id = useId();
  const titre = useRef<HTMLHeadingElement>(null);
  const [repliee, setRepliee] = useState(false);
  const { rendu, saisie, miroir } = figure;
  const { etat, reponse, erreur, couleursDeLErreur, long } = rendu;
  // Pendant un calcul, le dernier apercu reste, estompe ; un echec le remplace.
  const montree = reponse !== null && (etat === "pret" || etat === "calcul") ? reponse : null;
  // Un nouveau calcul sous un apercu qui reste : la roue le dit. Sans apercu, c'est le cadre « 1 a 3 s ».
  const recalcule = etat === "calcul" && montree !== null;
  // La colonne n'affiche que l'apercu : le lien des fichiers du rendu ne servirait a rien ici.
  const liens = useLiensDeFichiers(montree, "apercu");
  const vignette = useUrlLocale(figure.image?.fichier ?? null);
  const avecImage = figure.image !== null;
  const signalement = erreurDuNom(nom, avecImage);

  const appliquerReglage = (reglage: Partial<Reglages>): void => {
    figure.setSaisie((actuelle) => appliquer(actuelle, reglage));
  };
  // Un remede disparait avec le verdict qui le portait : le focus va au titre de la colonne plutot
  // que de retomber sur la page.
  const apresRemede = (): void => titre.current?.focus();
  // Un lien qui applique un reglage (celui d'un avertissement de l'outil, d'un refus ou du rapport)
  // devient du texte des qu'il est pose : le focus va au titre, comme sur la page d'une silhouette.
  const appliquerPuisTitre = (reglage: Partial<Reglages>): void => {
    appliquerReglage(reglage);
    apresRemede();
  };
  const basculerLeMiroir = (): void => figure.setMiroir(!miroir);
  const pastille = verdictDeLaColonne ? pastilleDuVerdict(verdictDeLaColonne) : null;

  const ligneNom = (
    <Ligne
      aide={aideDuNom(nom, filaments > 0 ? filaments : null)}
      aideId={`${id}-nom-aide`}
      erreur={signalement}
      libelle="Nom"
      pour={`${id}-nom`}
    >
      <input
        aria-describedby={`${id}-nom-aide`}
        aria-invalid={signalement !== null || undefined}
        autoCapitalize="words"
        autoComplete="off"
        className={cn(CHAMP, "w-full aria-invalid:border-destructive")}
        id={`${id}-nom`}
        maxLength={60}
        onChange={(event) => {
          const saisi = event.target.value;
          figure.setSaisie((actuelle) => ({ ...actuelle, nom: saisi }));
        }}
        placeholder={exempleDeNom(colonne)}
        spellCheck={false}
        type="text"
        value={saisie.nom}
      />
    </Ligne>
  );
  const ligneMiroir = (
    <Ligne libelle="Miroir" pour={`${id}-miroir`}>
      <span className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
        <input
          aria-describedby={`${id}-miroir-legende`}
          checked={miroir}
          className={INTERRUPTEUR}
          id={`${id}-miroir`}
          onChange={(event) => figure.setMiroir(event.target.checked)}
          type="checkbox"
        />
        <span id={`${id}-miroir-legende`}>{legendeDuMiroir(miroir)}</span>
      </span>
    </Ligne>
  );

  return (
    <section
      aria-busy={etat === "calcul"}
      aria-labelledby={`${id}-titre`}
      className={cn(CARTE, "flex flex-col gap-3")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 sm:flex sm:flex-wrap sm:items-baseline sm:gap-x-2.5">
          <h2
            className="rounded-sm text-[15px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            id={`${id}-titre`}
            ref={titre}
            tabIndex={-1}
          >
            {titreDeLaColonne(colonne)}
          </h2>
          <p className="text-[13px] text-muted-foreground">{legendeDeLaColonne(colonne)}</p>
        </div>
        {avecImage ? (
          <button
            aria-controls={`${id}-corps`}
            aria-expanded={!repliee}
            aria-label={`${repliee ? "Déplier" : "Replier"} ${figurineDe(colonne)}`}
            className={cn(
              BOUTON_SECONDAIRE,
              "size-11 shrink-0 px-0 text-muted-foreground lg:hidden",
            )}
            onClick={() => setRepliee((actuelle) => !actuelle)}
            type="button"
          >
            <Chevron haut={!repliee} />
          </button>
        ) : null}
      </div>

      {repliee ? (
        <div className="flex items-center gap-3 lg:hidden" data-resume="">
          {vignette ? (
            <img
              alt=""
              className="size-14 shrink-0 rounded-lg border border-border bg-white object-contain"
              src={vignette}
            />
          ) : null}
          <div className="min-w-0">
            <p className="truncate font-semibold">{nomOuColonne(nom, colonne)}</p>
            {reponse ? (
              <p className="mb-1 text-[13px] text-muted-foreground">
                {resumeDeLaColonneRepliee(reponse.rapport.hauteur_hors_tout_mm)}
              </p>
            ) : null}
            {pastille ? (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[13px] font-medium before:size-2 before:rounded-full before:bg-current before:content-['']",
                  pastille.ton === "bon"
                    ? "border-jugement-bonne/40 bg-jugement-bonne/10 text-jugement-bonne"
                    : "border-destructive/40 bg-destructive/[0.06] text-destructive",
                )}
              >
                {pastille.texte}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={cn("flex flex-col gap-3", repliee && "hidden lg:flex")} id={`${id}-corps`}>
        <CarteImage
          boutonChanger={figure.boutonChanger}
          choisir={figure.choisir}
          className=""
          erreur={figure.erreurImage}
          etiquette={`Image de ${figurineDe(colonne)}`}
          image={figure.image}
          integree
          origine={reponse?.rapport.origine_du_masque}
          preparation={figure.preparation}
        />

        <CarteReglages
          avecImage={avecImage}
          changer={figure.setSaisie}
          rapport={reponse?.rapport ?? null}
          saisie={saisie}
          serreLivres={{
            ligneNom,
            horsTout: figure.lecture.hauteurInvalide
              ? null
              : legendeHorsTout(figure.lecture.reglages.hauteur_mm),
            resume: resumeDesReglages(saisie, reponse?.rapport ?? null),
            suite: ligneMiroir,
            titre: `Réglages de ${figurineDe(colonne)}`,
          }}
        />

        {avecImage ? (
          <div className="flex flex-col gap-3">
            {/* Toujours la, pour que le lecteur d'ecran annonce un calcul ; vide, elle ne prend pas de place. */}
            <p
              className={cn("text-[13px] text-muted-foreground", !recalcule && "sr-only")}
              role="status"
            >
              {recalcule ? (
                <>
                  <Roue className="mr-2 align-[-3px]" />
                  calcul…
                </>
              ) : null}
            </p>

            {etat === "calcul" && long ? (
              <p className={ALERTE.grise}>
                Plus long que d'habitude : l'image est détaillée, ou un autre rendu passe avant. Le
                tien suit, sans rien à faire.
              </p>
            ) : null}

            {etat === "erreur" && erreur ? (
              <ErreurDuRendu
                appliquer={appliquerPuisTitre}
                couleursDeLErreur={couleursDeLErreur}
                erreur={erreur}
                relancer={() => {
                  rendu.relancer();
                  apresRemede();
                }}
                saisie={saisie}
              />
            ) : null}

            {miroir && montree ? (
              <span className="self-start rounded-full border border-border bg-card px-2.5 py-0.5 text-[12.5px] text-foreground">
                {MENTION_MIROIR}
              </span>
            ) : null}

            {montree ? (
              <ApercuRendu
                appliquer={appliquerPuisTitre}
                lienApercu={liens.apercu}
                reponse={montree}
                saisie={saisie}
                serreLivres={{
                  nom: nomOuColonne(nom, colonne),
                  grise: etat === "calcul",
                  verdict: verdictDeLaColonne ? (
                    <BlocDuVerdict
                      enCalcul={etat === "calcul"}
                      garder={(hauteur) => {
                        appliquerReglage({ hauteur_mm: hauteur });
                        apresRemede();
                      }}
                      retourner={() => {
                        basculerLeMiroir();
                        apresRemede();
                      }}
                      retournerA={(hauteur) => {
                        basculerLeMiroir();
                        appliquerReglage({ hauteur_mm: hauteur });
                        apresRemede();
                      }}
                      verdict={verdictDeLaColonne}
                    />
                  ) : null,
                }}
              />
            ) : null}

            {!montree && etat === "calcul" ? (
              <div className="flex min-h-40 flex-col items-center justify-center gap-2 rounded-lg border-[1.5px] border-dashed border-border bg-muted/40">
                <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[13.5px]">
                  <Roue />
                  calcul…
                </span>
                <span className="text-[13px] text-muted-foreground">1 à 3 s</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * La carte « Sur l'etagere » : les deux figurines a l'echelle dans leur Bookend, aux teintes
 * choisies (`silhouettes-etagere`). Au telephone elle prend toute la largeur de l'ecran et defile
 * sur 880 px, comme la planche ; a partir de `lg`, elle tient dans sa carte, 1 055 px au plus.
 */
function CarteEtagere({
  gauche,
  droite,
  miroirGauche,
  miroirDroite,
  toutes,
  teinteBase,
}: {
  gauche: RenduReponse | null;
  droite: RenduReponse | null;
  miroirGauche: boolean;
  miroirDroite: boolean;
  toutes: readonly LigneFilament[];
  teinteBase: TeinteBambu | null;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={`${id}-titre`}
      className="min-w-0 border-y border-border bg-card py-4 text-card-foreground max-md:-mx-4 md:rounded-lg md:border md:p-5"
    >
      <div className="flex items-baseline justify-between gap-3 max-md:px-4">
        <TitreCarte id={`${id}-titre`}>Sur l'étagère</TitreCarte>
        <span className="text-[13px] text-muted-foreground md:hidden">
          glisse pour voir la suite
        </span>
      </div>
      <p className="mt-1 mb-3 text-[13px] text-muted-foreground max-md:px-4">
        Vue de face, à l'échelle, barres poussées contre le mur. En rouge, ce qui heurte le mur. Les
        teintes sont celles des filaments choisis plus bas.
      </p>
      <div className="overflow-x-auto lg:overflow-visible">
        <Etagere
          className="min-w-[880px] lg:mx-auto lg:min-w-0 lg:max-w-[1055px]"
          droite={droite && { ...droite, miroir: miroirDroite }}
          gauche={gauche && { ...gauche, miroir: miroirGauche }}
          teinteBase={teinteBase}
          teintes={toutes}
        />
      </div>
    </section>
  );
}
